type RecsysImpressionContext = {
  requestUuid: string;
  sectionId: string | number;
  sectionPosition: number;
  itemType: string;
  itemId: string | number;
  itemPosition: number;
};

type RecsysImpressionState = {
  impressionId: string;
  firstVisibleAtMs: number;
  clicked: boolean;
  serveSent: boolean;
};

const sectionServeKeys = new Set<string>();
const sectionListViewStreamSessionKeys = new Set<string>();
const itemListViewKeys = new Set<string>();
const lastEventAtByKey = new Map<string, number>();
const lastSectionLastViewPositionByRequest = new Map<string, number>();
const impressionStateByKey = new Map<string, RecsysImpressionState>();
const requestSeenAt = new Map<string, number>();
const MAX_TRACKED_REQUESTS = 1000;
const MAX_TRACKED_STREAM_SESSIONS = 100;

import { createClientUuid } from "../createClientUuid";

function makeSectionServeKey(
  requestUuid: string,
  sectionId: string | number,
): string {
  return `${requestUuid}:${sectionId}`;
}

function makeImpressionKey(context: RecsysImpressionContext): string {
  return `${context.requestUuid}:${context.sectionId}:${context.sectionPosition}:${context.itemType}:${context.itemId}:${context.itemPosition}`;
}

function shouldEmitThrottled(key: string, throttleMs: number): boolean {
  const now = Date.now();
  const last = lastEventAtByKey.get(key) ?? 0;

  if (now - last < throttleMs) {
    return false;
  }

  lastEventAtByKey.set(key, now);
  return true;
}

function pruneRequestKeys(requestUuid: string): void {
  const requestPrefix = `${requestUuid}:`;
  const itemLastViewPrefix = `item_last_view:${requestUuid}:`;
  const sectionLastViewKey = `section_last_view:${requestUuid}`;

  for (const key of sectionServeKeys) {
    if (key.startsWith(requestPrefix)) {
      sectionServeKeys.delete(key);
    }
  }

  for (const key of itemListViewKeys) {
    if (key.startsWith(requestPrefix)) {
      itemListViewKeys.delete(key);
    }
  }

  for (const key of impressionStateByKey.keys()) {
    if (key.startsWith(requestPrefix)) {
      impressionStateByKey.delete(key);
    }
  }

  for (const key of lastEventAtByKey.keys()) {
    if (key === sectionLastViewKey || key.startsWith(itemLastViewPrefix)) {
      lastEventAtByKey.delete(key);
    }
  }

  lastSectionLastViewPositionByRequest.delete(requestUuid);
  requestSeenAt.delete(requestUuid);
}

function evictOldRequestsIfNeeded(): void {
  while (requestSeenAt.size > MAX_TRACKED_REQUESTS) {
    let oldestRequestUuid: string | undefined;
    let oldestSeenAt = Number.POSITIVE_INFINITY;

    for (const [requestUuid, seenAt] of requestSeenAt.entries()) {
      if (seenAt < oldestSeenAt) {
        oldestSeenAt = seenAt;
        oldestRequestUuid = requestUuid;
      }
    }

    if (!oldestRequestUuid) {
      return;
    }

    pruneRequestKeys(oldestRequestUuid);
  }
}

function touchRequest(requestUuid: string): void {
  requestSeenAt.set(requestUuid, Date.now());
  evictOldRequestsIfNeeded();
}

export function shouldEmitRecsysSectionServe(
  requestUuid: string,
  sectionId: string | number,
): boolean {
  touchRequest(requestUuid);

  const key = makeSectionServeKey(requestUuid, sectionId);
  if (sectionServeKeys.has(key)) {
    return false;
  }

  sectionServeKeys.add(key);
  return true;
}

export function getRecsysImpression(
  context: RecsysImpressionContext,
): RecsysImpressionState | undefined {
  touchRequest(context.requestUuid);
  return impressionStateByKey.get(makeImpressionKey(context));
}

export function getOrCreateRecsysImpression(
  context: RecsysImpressionContext,
): RecsysImpressionState {
  touchRequest(context.requestUuid);

  const key = makeImpressionKey(context);
  const existing = impressionStateByKey.get(key);
  if (existing) {
    return existing;
  }

  const nextState: RecsysImpressionState = {
    impressionId: createClientUuid(),
    firstVisibleAtMs: Date.now(),
    clicked: false,
    serveSent: false,
  };

  impressionStateByKey.set(key, nextState);
  return nextState;
}

export function markRecsysImpressionClicked(
  context: RecsysImpressionContext,
): RecsysImpressionState {
  const impression = getOrCreateRecsysImpression(context);
  impression.clicked = true;
  return impression;
}

export function shouldEmitRecsysSectionListView(
  requestUuid: string,
  streamSessionId: string,
): boolean {
  touchRequest(requestUuid);

  if (sectionListViewStreamSessionKeys.has(streamSessionId)) {
    return false;
  }

  sectionListViewStreamSessionKeys.add(streamSessionId);

  if (sectionListViewStreamSessionKeys.size > MAX_TRACKED_STREAM_SESSIONS) {
    const oldest = sectionListViewStreamSessionKeys.values().next().value;
    if (oldest) sectionListViewStreamSessionKeys.delete(oldest);
  }

  return true;
}

export function shouldEmitRecsysSectionLastView(
  requestUuid: string,
  lastVisiblePosition: number,
  throttleMs = 300,
): boolean {
  touchRequest(requestUuid);

  if (lastSectionLastViewPositionByRequest.get(requestUuid) === lastVisiblePosition) {
    return false;
  }

  const shouldEmit = shouldEmitThrottled(
    `section_last_view:${requestUuid}`,
    throttleMs,
  );

  if (!shouldEmit) {
    return false;
  }

  lastSectionLastViewPositionByRequest.set(requestUuid, lastVisiblePosition);
  return true;
}

export function shouldEmitRecsysItemListView(
  requestUuid: string,
  sectionId: string | number,
): boolean {
  touchRequest(requestUuid);

  const key = `${requestUuid}:${sectionId}`;
  if (itemListViewKeys.has(key)) {
    return false;
  }

  itemListViewKeys.add(key);
  return true;
}

export function shouldEmitRecsysItemLastView(
  requestUuid: string,
  sectionId: string | number,
  throttleMs = 300,
): boolean {
  touchRequest(requestUuid);
  return shouldEmitThrottled(`item_last_view:${requestUuid}:${sectionId}`, throttleMs);
}
